import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Home as HomeIcon,
  Flight as AwayIcon,
  Info as InfoIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/environment';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { getImpersonationHeaders } from '../context/ImpersonationContext';
import { getEligibleCompetitionSummary, formatCompetitionDateRange } from '../utils/eligibilityDisplay';
import { isChampionshipCompetition } from '../utils/championshipEvent';

export default function EligibilityTracker({ skaterId, skaterName, onClose }) {
  const { getIdToken } = useFirebaseAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [showCompetitions, setShowCompetitions] = useState(true);
  const [showEventPlanner, setShowEventPlanner] = useState(true);
  const [updatingPlanned, setUpdatingPlanned] = useState({});
  const [showCalculator, setShowCalculator] = useState(false);
  const [expectedPlacements, setExpectedPlacements] = useState({}); // { competitionId: placement (1-6) }
  const [levelsCompeted, setLevelsCompeted] = useState(1); // Number of levels competed at (1-3), tie breaker = levels - 1

  const loadEligibility = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken(true);
      
      const response = await fetch(`${API_BASE_URL}/skaters/${skaterId}/eligibility`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load eligibility data');
      }

      const data = await response.json();
      setEligibilityData(data);
    } catch (err) {
      console.error('Error loading eligibility:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skaterId, getIdToken]);

  useEffect(() => {
    loadEligibility();
  }, [loadEligibility]);

  const handleTogglePlannedEvent = async (competitionId, isCurrentlyPlanned) => {
    try {
      setUpdatingPlanned((prev) => ({ ...prev, [competitionId]: true }));
      const token = await getIdToken(true);

      if (isCurrentlyPlanned) {
        // Remove from planned
        await fetch(`${API_BASE_URL}/skaters/${skaterId}/planned-events/${competitionId}`, {
          method: 'DELETE',
          headers: { 
            Authorization: `Bearer ${token}`,
            ...getImpersonationHeaders(),
          },
        });
      } else {
        // Add to planned
        await fetch(`${API_BASE_URL}/skaters/${skaterId}/planned-events`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...getImpersonationHeaders(),
          },
          body: JSON.stringify({ competitionId }),
        });
      }

      // Reload eligibility data
      await loadEligibility();
    } catch (err) {
      console.error('Error updating planned event:', err);
    } finally {
      setUpdatingPlanned((prev) => ({ ...prev, [competitionId]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!eligibilityData) {
    return null;
  }

  const { eligibility, requirements, competitionsAttended, upcomingCompetitions, plannedEvents } = eligibilityData;
  const progressPercent = Math.min(100, ((eligibility.uniqueClubsCompeted || 0) / 3) * 100);

  // Championship is invite-only and does not count toward series qualification
  const qualifyingUpcoming = upcomingCompetitions.filter((c) => !isChampionshipCompetition(c));
  const plannedUpcoming = qualifyingUpcoming.filter((c) => plannedEvents.includes(c.id));
  const projectedCompetitions =
    (eligibility.countingCompetitions ?? eligibility.uniqueClubsCompeted ?? eligibility.totalCompetitions ?? 0) +
    plannedUpcoming.length;

  // Calculate projected unique clubs
  const currentClubIds = new Set(
    competitionsAttended.filter((c) => !c.excludedFromPointsReason).map((c) => c.hostClubId).filter(Boolean)
  );
  const plannedClubIds = new Set(plannedUpcoming.map((c) => c.clubId).filter(Boolean));
  const allClubIds = new Set([...currentClubIds, ...plannedClubIds]);
  const projectedUniqueClubs = allClubIds.size;
  const projectedMeetsMinimum = projectedUniqueClubs >= 3;
  // Count unique NEW clubs from planned (not events, but unique clubs not already attended)
  const newClubIdsFromPlanned = new Set(
    plannedUpcoming.filter((c) => c.clubId && !currentClubIds.has(c.clubId)).map((c) => c.clubId)
  );
  const newClubsFromPlanned = newClubIdsFromPlanned.size;

  // Helper: convert placement to points
  const placementToPoints = (place) => {
    if (!place || place < 1 || place > 6) return 0;
    return 7 - place; // 1st=6, 2nd=5, 3rd=4, 4th=3, 5th=2, 6th=1
  };

  // Calculate "What-If" projected points
  const calculateProjectedPoints = () => {
    // Start with current best scores by club (from competitions attended)
    const bestScoresByClub = {};
    const competitionsByClub = {}; // Track which competitions are at each club
    
    competitionsAttended.forEach((comp) => {
      if (comp.excludedFromPointsReason) return;
      // Use hostClubId if available, otherwise use competition ID as unique key
      const clubKey = comp.hostClubId || `attended-${comp.competitionId}`;
      if (!competitionsByClub[clubKey]) {
        competitionsByClub[clubKey] = [];
      }
      competitionsByClub[clubKey].push({ name: comp.competitionName, points: comp.points, type: 'attended' });
      if (!bestScoresByClub[clubKey] || comp.points > bestScoresByClub[clubKey]) {
        bestScoresByClub[clubKey] = comp.points;
      }
    });

    // Add expected scores from planned events (without tie breaker - that's separate)
    plannedUpcoming.forEach((comp) => {
      // Use clubId if available, otherwise use competition ID as unique key
      // This ensures competitions without clubId don't incorrectly group together
      const clubKey = comp.clubId || `planned-${comp.id}`;
      const expectedPlace = expectedPlacements[comp.id];
      if (expectedPlace) {
        const expectedPoints = placementToPoints(expectedPlace);
        if (!competitionsByClub[clubKey]) {
          competitionsByClub[clubKey] = [];
        }
        competitionsByClub[clubKey].push({ name: comp.name, points: expectedPoints, type: 'planned' });
        // Only use if better than existing score at this club
        if (!bestScoresByClub[clubKey] || expectedPoints > bestScoresByClub[clubKey]) {
          bestScoresByClub[clubKey] = expectedPoints;
        }
      }
    });

    // Get top 3 scores (placement points only)
    const allScores = Object.values(bestScoresByClub).sort((a, b) => b - a);
    const top3 = allScores.slice(0, 3);
    const placementPoints = top3.reduce((sum, s) => sum + s, 0);
    
    // Calculate tie breaker points separately (levels competed - 1)
    const tieBreakerPoints = Math.max(0, levelsCompeted - 1);
    const totalPoints = placementPoints + tieBreakerPoints;
    
    // Find clubs with multiple competitions (duplicates)
    const duplicateClubs = Object.entries(competitionsByClub)
      .filter(([, comps]) => comps.length > 1)
      .map(([clubKey, comps]) => ({
        clubKey,
        competitions: comps,
        bestScore: bestScoresByClub[clubKey],
      }));

    return {
      bestScoresByClub,
      top3,
      placementPoints,
      tieBreakerPoints,
      totalPoints,
      allScores,
      uniqueClubCount: Object.keys(bestScoresByClub).length,
      duplicateClubs,
    };
  };

  const projectedCalc = calculateProjectedPoints();
  const hasAnyExpectedPlacements = Object.keys(expectedPlacements).some(
    (id) => plannedEvents.includes(id) && expectedPlacements[id]
  );

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
      {/* Paused/Disqualified Alert */}
      {(eligibility.isPaused || eligibility.isDisqualified) && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 28 }
          }}
          icon={<CancelIcon />}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {eligibility.isDisqualified ? 'Disqualified from Championships' : 'Season Participation Paused'}
          </Typography>
          <Typography variant="body2">
            {eligibility.isDisqualified 
              ? 'This skater has been disqualified from the championship series for this season.'
              : 'This skater\'s participation has been paused for this season.'
            }
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            Please contact the competition coordinator for more information.
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: eligibility.isEligible
                ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                : eligibility.isPaused || eligibility.isDisqualified
                  ? 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
            }}
          >
            <TrophyIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Championship Eligibility
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {eligibilityData.seasonName}
            </Typography>
          </Box>
        </Box>
        {eligibility.isEligible ? (
          <Chip
            icon={<CheckIcon />}
            label="Eligible"
            color="success"
            sx={{ fontWeight: 600, px: 1 }}
          />
        ) : eligibility.isPaused || eligibility.isDisqualified ? (
          <Chip
            icon={<CancelIcon />}
            label={eligibility.isDisqualified ? 'Disqualified' : 'Paused'}
            color="error"
            sx={{ fontWeight: 600, px: 1 }}
          />
        ) : (
          <Chip
            icon={<WarningIcon />}
            label="Not Yet Eligible"
            color="warning"
            sx={{ fontWeight: 600, px: 1 }}
          />
        )}
      </Box>

      {/* Progress Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Unique Clubs With Counting Scores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {eligibility.uniqueClubsCompeted || 0} / 3 minimum
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 12,
            borderRadius: 6,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 6,
              background:
                progressPercent >= 100
                  ? 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)'
                  : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            },
          }}
        />
        {(eligibility.uniqueClubsCompeted || 0) < 3 && (
          <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 500 }}>
            {requirements.minimumCompetitions.message}
          </Typography>
        )}
      </Box>

      {/* Requirements Checklist */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
          Requirements
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {requirements.minimumCompetitions.met ? (
                <CheckIcon sx={{ color: 'success.main' }} />
              ) : (
                <CancelIcon sx={{ color: 'grey.400' }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: requirements.minimumCompetitions.met ? 600 : 400 }}>
                {requirements.minimumCompetitions.message}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {requirements.paidRegistration.met ? (
                  <CheckIcon sx={{ color: 'success.main' }} />
                ) : (
                  <CancelIcon sx={{ color: 'error.main' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: requirements.paidRegistration.met ? 600 : 400 }}>
                  {requirements.paidRegistration.message}
                </Typography>
              </Box>
              {requirements.paidRegistration.met && eligibility.registrationPaidAt && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 3.75 }}>
                  Payment date:{' '}
                  {new Date(eligibility.registrationPaidAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
              )}
              {requirements.paidRegistration.met && !eligibility.registrationPaidAt && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 3.75 }}>
                  Payment date not on file (older registration)
                </Typography>
              )}
            </Box>
          </Grid>
          {requirements.paymentTiming?.message && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {requirements.paymentTiming.message}
                {eligibility.registrationPaidAt
                  ? ` Season fee paid ${new Date(eligibility.registrationPaidAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}.`
                  : ''}
              </Alert>
            </Grid>
          )}
          {requirements.eligibilityStatus && !requirements.eligibilityStatus.met && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CancelIcon sx={{ color: 'error.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {requirements.eligibilityStatus.message}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Scoring Summary */}
      <Box sx={{ mb: 4, p: 2.5, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Your Scoring
          </Typography>
          {plannedUpcoming.length > 0 && (
            <Chip size="small" label={`+${plannedUpcoming.length} planned`} color="primary" variant="outlined" />
          )}
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Total Points</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {eligibility.totalPoints}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Top 3 Scores</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {eligibility.top3Scores.join(' + ') || '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Unique Clubs</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {eligibility.uniqueClubsCompeted || 0}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5, fontWeight: 500 }}>
                  / 3
                </Typography>
              </Typography>
              {newClubsFromPlanned > 0 && (
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                  +{newClubsFromPlanned}
                </Typography>
              )}
            </Box>
            {plannedUpcoming.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                → {projectedUniqueClubs} with planned
              </Typography>
            )}
          </Grid>
          <Grid item xs={6} sm={3}>
            {(() => {
              const summary = getEligibleCompetitionSummary(eligibility);
              return (
                <>
                  <Typography variant="body2" color="text.secondary">{summary.label}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: summary.excluded > 0 ? 'warning.dark' : 'text.primary' }}>
                      {summary.counting}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      / {summary.attended || 0}
                    </Typography>
                  </Box>
                  {summary.excludedLabel && (
                    <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600, display: 'block' }}>
                      {summary.excludedLabel}
                    </Typography>
                  )}
                  {plannedUpcoming.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      → {projectedCompetitions} with planned
                    </Typography>
                  )}
                </>
              );
            })()}
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} icon={<InfoIcon />}>
          <Typography variant="body2">
            <strong>Scoring Rule:</strong> Only your best score from each host club counts toward your top 3.
            {eligibility.uniqueClubsCompeted < 3 && ' Attending events at different clubs maximizes your scoring potential!'}
          </Typography>
        </Alert>
      </Box>

      {/* Tie Breaker Points Calculator */}
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            cursor: 'pointer', 
            mb: 2,
            p: 1.5,
            pl: 2,
            border: '1px solid',
            borderColor: showCalculator ? 'secondary.main' : 'grey.300',
            borderRadius: 2,
            bgcolor: showCalculator ? 'secondary.50' : 'grey.50',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'secondary.main',
              bgcolor: 'secondary.50',
            }
          }}
          onClick={() => setShowCalculator(!showCalculator)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CalculateIcon sx={{ color: 'secondary.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                Tie Breaker Points Calculator
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {showCalculator ? 'Click to collapse' : 'Click to project your tie breaker points'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" sx={{ color: 'secondary.main' }}>
            {showCalculator ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Paper>
        <Collapse in={showCalculator}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2.5, 
              bgcolor: 'secondary.50', 
              border: '1px solid', 
              borderColor: 'secondary.200',
              borderRadius: 2 
            }}
          >
            {plannedUpcoming.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select expected placements for your planned events to see your projected tie breaker points.
                </Typography>
                
                {/* Planned Events with Placement Selectors */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  {plannedUpcoming.map((comp) => {
                  const compDate = new Date(comp.date);
                  const hasExistingScoreAtClub = competitionsAttended.some(
                    (c) => c.hostClubId === comp.clubId
                  );
                  const existingScoreAtClub = competitionsAttended.find(
                    (c) => c.hostClubId === comp.clubId
                  );
                  
                  return (
                    <Box 
                      key={comp.id}
                      sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {comp.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {compDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {comp.clubName && ` • ${comp.clubName}`}
                        </Typography>
                        {hasExistingScoreAtClub && (
                          <Typography variant="caption" display="block" sx={{ color: 'warning.main', fontWeight: 500 }}>
                            Note: You already have {existingScoreAtClub.points} pts at this club
                          </Typography>
                        )}
                      </Box>
                      
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Placement</InputLabel>
                        <Select
                          value={expectedPlacements[comp.id] || ''}
                          label="Placement"
                          onChange={(e) => setExpectedPlacements(prev => ({
                            ...prev,
                            [comp.id]: e.target.value
                          }))}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          <MenuItem value={1}>1st (6 pts)</MenuItem>
                          <MenuItem value={2}>2nd (5 pts)</MenuItem>
                          <MenuItem value={3}>3rd (4 pts)</MenuItem>
                          <MenuItem value={4}>4th (3 pts)</MenuItem>
                          <MenuItem value={5}>5th (2 pts)</MenuItem>
                          <MenuItem value={6}>6th (1 pt)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </Box>

              {/* Tie Breaker Points Section */}
              <Box sx={{ 
                p: 2, 
                mb: 3,
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tie Breaker Points
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  How many levels will this skater compete at? (+1 point for each additional level)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 2 }}>
                  e.g., Aspire 1 → Aspire 2 in the same competition year
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Levels Competed</InputLabel>
                    <Select
                      value={levelsCompeted}
                      label="Levels Competed"
                      onChange={(e) => setLevelsCompeted(e.target.value)}
                    >
                      <MenuItem value={1}>1 level (0 pts)</MenuItem>
                      <MenuItem value={2}>2 levels (+1 pt)</MenuItem>
                      <MenuItem value={3}>3 levels (+2 pts)</MenuItem>
                    </Select>
                  </FormControl>
                  {levelsCompeted > 1 && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      +{levelsCompeted - 1} tie breaker {levelsCompeted - 1 === 1 ? 'point' : 'points'}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Projected Results */}
              {hasAnyExpectedPlacements && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'success.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'success.200'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <TrendingUpIcon sx={{ color: 'success.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      Projected Results
                    </Typography>
                  </Box>
                  
                  {/* Breakdown */}
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={8}>
                        <Typography variant="body2" color="text.secondary">
                          Placement Points (Top 3: {projectedCalc.top3.join(' + ')})
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {projectedCalc.placementPoints}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" color="text.secondary">
                          Tie Breaker Points ({levelsCompeted} {levelsCompeted === 1 ? 'level' : 'levels'})
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                          +{projectedCalc.tieBreakerPoints}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 0.5 }}>
                          <Grid container>
                            <Grid item xs={8}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Total Projected
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: 'success.main' }}>
                                {projectedCalc.totalPoints}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Current vs Projected */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Current Total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {eligibility.totalPoints}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Projected Total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {projectedCalc.totalPoints}
                        {projectedCalc.totalPoints > eligibility.totalPoints && (
                          <Typography component="span" variant="body2" sx={{ ml: 1, color: 'success.dark' }}>
                            (+{projectedCalc.totalPoints - eligibility.totalPoints})
                          </Typography>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {/* Show duplicate club groupings */}
                  {projectedCalc.duplicateClubs && projectedCalc.duplicateClubs.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.75rem' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Same-club competitions (only best score counts):
                      </Typography>
                      {projectedCalc.duplicateClubs.map((group, idx) => (
                        <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                          • {group.competitions.map(c => `${c.name} (${c.points}pts)`).join(' & ')} → using {group.bestScore}pts
                        </Typography>
                      ))}
                    </Alert>
                  )}
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    * Only the best score from each unique club counts toward placement points.
                  </Typography>
                </Box>
              )}

              {!hasAnyExpectedPlacements && plannedUpcoming.length > 0 && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Select expected placements above to see your projected tie breaker points.
                </Alert>
              )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Plan events in the Event Planner below to project your tie breaker points.
                </Typography>
                <Alert severity="info" sx={{ borderRadius: 2, textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    How to use the calculator:
                  </Typography>
                  <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
                    <li>Mark events as "Plan to Attend" in the Event Planner section below</li>
                    <li>Come back here to select expected placements for each planned event</li>
                    <li>See your projected placement points and tie breaker points</li>
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>
        </Collapse>
      </Box>

      {/* Competition History */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
          onClick={() => setShowCompetitions(!showCompetitions)}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Competition History ({eligibility.totalEvents || competitionsAttended.length})
          </Typography>
          <IconButton size="small">
            {showCompetitions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={showCompetitions}>
          {competitionsAttended.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No competitions attended yet this season. Check the event planner below to plan your events!
            </Alert>
          ) : (
            <TableContainer>
              {competitionsAttended.some((c) =>
                String(c.excludedFromPointsReason || '').includes('after this event ended')
              ) && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">
                    Results from events that already ended before you paid the season fee do not count toward series
                    points. If you paid during the event, the result still counts. Hover the warning icon in the Counts
                    column for details.
                  </Typography>
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Competition</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Host Club</TableCell>
                    <TableCell align="center">Place</TableCell>
                    <TableCell align="center">Points</TableCell>
                    <TableCell align="center">Counts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {competitionsAttended.map((comp, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor: comp.counts ? 'success.50' : 'transparent',
                        opacity: comp.counts ? 1 : 0.6,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {comp.isHomeClub ? (
                            <Tooltip title="Your home club">
                              <HomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Away club">
                              <AwayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Tooltip>
                          )}
                          <Typography variant="body2">{comp.competitionName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const paidAfterEvent = String(comp.excludedFromPointsReason || '').includes('after this event ended');
                          return (
                            <Tooltip
                              title={
                                paidAfterEvent
                                  ? 'This event ended before the season fee was paid'
                                  : 'Event dates (last day is used for payment timing)'
                              }
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  color: paidAfterEvent ? 'warning.dark' : 'text.primary',
                                }}
                              >
                                {formatCompetitionDateRange(comp)}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {comp.event || comp.level || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {comp.hostClubName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{comp.place || '—'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: comp.counts ? 700 : 400 }}>
                          {comp.points}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {comp.counts ? (
                          <Tooltip title="This score counts toward your top 3">
                            <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          </Tooltip>
                        ) : comp.excludedFromPointsReason ? (
                          <Tooltip title={comp.excludedFromPointsReason}>
                            <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                          </Tooltip>
                        ) : comp.shadowedBy ? (
                          <Tooltip title={`Shadowed by higher score at ${comp.shadowedBy}`}>
                            <Typography variant="caption" color="text.secondary">
                              Shadowed
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Event Planner */}
      <Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 2 }}
          onClick={() => setShowEventPlanner(!showEventPlanner)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CalendarIcon sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
              Event Planner
            </Typography>
            {plannedEvents.length > 0 && (
              <Chip size="small" label={`${plannedEvents.length} planned`} color="primary" variant="outlined" />
            )}
          </Box>
          <IconButton size="small">
            {showEventPlanner ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={showEventPlanner}>
          {/* Projected Status */}
          {plannedUpcoming.length > 0 && (
            <Alert
              severity={projectedMeetsMinimum ? 'success' : 'info'}
              sx={{ mb: 2, borderRadius: 2 }}
              icon={<StarIcon />}
            >
              <Typography variant="body2">
                <strong>If you attend your {plannedUpcoming.length} planned qualifying event{plannedUpcoming.length > 1 ? 's' : ''}:</strong>{' '}
                You will have counting scores at {projectedUniqueClubs} unique club{projectedUniqueClubs !== 1 ? 's' : ''}.
                {projectedMeetsMinimum
                  ? ' You will meet the 3 unique club requirement!'
                  : ` You'll need ${3 - projectedUniqueClubs} more unique club${3 - projectedUniqueClubs === 1 ? '' : 's'}.`}
                {!projectedMeetsMinimum && projectedUniqueClubs < projectedCompetitions && (
                  ' Consider adding events at different clubs to maximize your scoring potential.'
                )}
              </Typography>
            </Alert>
          )}

          {upcomingCompetitions.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No upcoming competitions scheduled for this season.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {upcomingCompetitions.map((comp) => {
                const isChampionship = isChampionshipCompetition(comp);
                const isPlanned = plannedEvents.includes(comp.id);
                const isUpdating = updatingPlanned[comp.id];
                const compDate = new Date(comp.date);

                // Check if this club already has a planned event or was already attended
                // Only check for duplicates if clubId is defined (to avoid false matches on undefined)
                const plannedUpcomingAtSameClub = comp.clubId ? qualifyingUpcoming.filter(
                  (c) => c.clubId === comp.clubId && plannedEvents.includes(c.id) && c.id !== comp.id
                ) : [];
                const attendedAtSameClub = comp.clubId ? competitionsAttended.filter(
                  (c) => c.hostClubId === comp.clubId && !c.excludedFromPointsReason
                ) : [];
                const hasDuplicateClub = !isChampionship && isPlanned && (plannedUpcomingAtSameClub.length > 0 || attendedAtSameClub.length > 0);

                return (
                  <Grid item xs={12} sm={6} key={comp.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: isChampionship
                          ? 'secondary.light'
                          : hasDuplicateClub
                            ? 'warning.main'
                            : isPlanned
                              ? 'primary.main'
                              : 'divider',
                        bgcolor: isChampionship
                          ? 'grey.50'
                          : hasDuplicateClub
                            ? 'warning.50'
                            : isPlanned
                              ? 'primary.50'
                              : 'transparent',
                        transition: 'all 0.2s',
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {comp.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {compDate.toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </Typography>
                            {comp.clubName && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  fontWeight: 500,
                                  color: hasDuplicateClub ? 'warning.dark' : 'text.secondary',
                                }}
                              >
                                Host: {comp.clubName}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              {comp.isHomeClub ? (
                                <Chip
                                  size="small"
                                  icon={<HomeIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Your Club"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  icon={<AwayIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Away"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                  variant="outlined"
                                />
                              )}
                              {isChampionship && (
                                <Chip
                                  size="small"
                                  icon={<TrophyIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Invite only"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                              {hasDuplicateClub && (
                                <Tooltip title="You already have a score or planned event at this club. Only your best score from each club counts!">
                                  <Chip
                                    size="small"
                                    icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                                    label="Duplicate Club"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                    color="warning"
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            {isChampionship && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                Does not count toward series qualification
                              </Typography>
                            )}
                          </Box>
                          {isChampionship ? (
                            isPlanned ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => handleTogglePlannedEvent(comp.id, true)}
                                disabled={isUpdating}
                                startIcon={isUpdating ? <CircularProgress size={14} /> : <RemoveIcon />}
                                sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                              >
                                Remove
                              </Button>
                            ) : null
                          ) : (
                            <Button
                              size="small"
                              variant={isPlanned ? 'contained' : 'outlined'}
                              color={hasDuplicateClub ? 'warning' : isPlanned ? 'primary' : 'inherit'}
                              onClick={() => handleTogglePlannedEvent(comp.id, isPlanned)}
                              disabled={isUpdating}
                              startIcon={isUpdating ? <CircularProgress size={14} /> : isPlanned ? <RemoveIcon /> : <AddIcon />}
                              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                            >
                              {isPlanned ? 'Planned' : 'Plan'}
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Collapse>
      </Box>

      {/* Motivational Footer */}
      {!eligibility.isEligible && !eligibility.isPaused && !eligibility.isDisqualified && qualifyingUpcoming.length > 0 && (
        <Alert
          severity="info"
          sx={{ mt: 3, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}
          icon={<TrophyIcon sx={{ color: 'primary.main' }} />}
        >
          <Typography variant="body2" sx={{ color: 'primary.dark' }}>
            <strong>Keep going!</strong> Complete the requirements above to qualify for the championship.
            {eligibility.uniqueClubsCompeted < 3 &&
              ' Tip: Attending events at 3 different clubs ensures all your best scores count!'}
          </Typography>
        </Alert>
      )}
      {!eligibility.isEligible && !eligibility.isPaused && !eligibility.isDisqualified && qualifyingUpcoming.length === 0 && (
        <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            Qualifying series events for this season are finished. The championship is invite-only for skaters who
            earned counting scores at 3 unique clubs.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
}
